import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Zap, 
  Shield, 
  ArrowRight,
  Eye,
  Clock
} from 'lucide-react';
import GridScaffold from './GridScaffold';
import { PreSignatureDemo } from './PreSignatureDemos';
import { SignatureDemo } from './SignatureDemos';
// Post-signature demo removed


// Design system colors
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d1d1',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  border: '#e5e7eb'
};

// Font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  }
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  },
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: { duration: 0.2, ease: "easeIn" }
  }
};

// Interactive Feature Card Component for Pre-Signature
const FeatureCard = ({ icon: Icon, title, description, delay = 0, demoType, selectedDemo, onSelectDemo, isActive }) => (
  <motion.div
    className="feature-card group cursor-pointer"
    style={{
      background: isActive ? `linear-gradient(135deg, ${colors.accentLight} 0%, rgba(30, 64, 175, 0.15) 100%)` : colors.white,
      border: `1px solid ${isActive ? colors.accent : colors.border}`,
      borderRadius: '12px',
      padding: '24px',
      boxShadow: isActive ? '0 4px 12px rgba(30, 64, 175, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease'
    }}
    variants={itemVariants}
    initial="hidden"
    animate="visible"
    transition={{ delay }}
    whileHover={{ 
      y: -2,
      boxShadow: isActive ? '0 6px 16px rgba(30, 64, 175, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
      borderColor: isActive ? colors.accentHover : '#d1d5db',
      transition: { duration: 0.2 }
    }}
    onClick={() => onSelectDemo && onSelectDemo(demoType)}
  >
    <div className="flex items-start">
      <div 
        className="mr-4 h-12 w-12 rounded-full flex items-center justify-center transition-all duration-300"
        style={{ 
          backgroundColor: isActive ? colors.accent : colors.accentLight,
          transform: isActive ? 'scale(1.05)' : 'scale(1)'
        }}
      >
        <Icon size={24} style={{ color: isActive ? colors.white : colors.accent }} />
      </div>
      <div className="flex-1">
        <h3 
          className="text-lg font-semibold mb-2 transition-colors duration-300" 
          style={{ 
            color: isActive ? colors.accent : colors.gray900, 
            ...fontStyles.heading 
          }}
        >
          {title}
        </h3>
        <p 
          className="text-base leading-relaxed transition-colors duration-300"
          style={{ color: colors.gray700, ...fontStyles.body }}
        >
          {description}
        </p>
        {isActive && (
          <div className="mt-3 flex items-center text-sm font-medium" style={{ color: colors.accent }}>
            <Eye size={14} className="mr-1.5" />
            Viewing Demo
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

// Demo Container Component
const DemoContainer = ({ children, title }) => (
  <motion.div
    className="demo-container"
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
      height: '100%',
      minHeight: '650px'
    }}
    variants={itemVariants}
    whileHover={{ 
      y: -3,
      transition: { duration: 0.3 }
    }}
  >
    {/* Demo Header */}
    <div 
      className="h-12 flex items-center px-5 border-b"
      style={{
        background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
        borderColor: colors.border
      }}
    >
      <div className="flex space-x-1.5 mr-5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
      </div>
      <div 
        className="text-xs font-medium flex items-center" 
        style={{ color: colors.gray500, ...fontStyles.medium }}
      >
        {title}
      </div>
    </div>
    
    {/* Demo Content */}
    <div className="p-0" style={{ height: 'calc(100% - 48px)', overflow: 'hidden' }}>
      {children}
    </div>
  </motion.div>
);

// Tab content configurations
const tabContent = {
  'pre-signature': {
    title: 'Pre-Signature',
    subtitle: 'AI Contract Generation',
    description: 'Simply describe what you need and watch AI generate a complete, legally-binding contract tailored for your business in seconds.',
    features: [] // No feature cards for simplified demo
  },
  'signature': {
    title: 'Signature',
    subtitle: 'World\'s Fastest E-Signature',
    description: 'One-click signatures that are legally binding worldwide. Faster than DocuSign, simpler than HelloSign, more secure than Adobe Sign.',
    features: [
      {
        icon: Zap,
        title: 'Complete Signing Experience',
        description: 'Watch the full end-to-end process:\nSmart auto-fill + instant signature in 8 seconds.',
        demoType: 'complete-experience',
        isPrimary: true
      },
      {
        icon: Shield,
        title: 'Legally Binding Everywhere',
        description: 'UETA, ESIGN Act compliant signatures.\nValid in 180+ countries worldwide.',
        demoType: 'compliance-demo'
      },
      {
        icon: Clock,
        title: 'Instant Audit Trail',
        description: 'Real-time legal documentation.\nComplete chain of custody in milliseconds.',
        demoType: 'audit-demo'
      }
    ],
    ctaButton: {
      text: 'Start Signing Now',
      url: 'https://signpact.ai/signup',
      highlight: true
    }
  }
};



// BandedTabs wrapper component (in-flow, spans rails)
const BandedTabs = ({ children }) => (
  <div className="mx-auto w-full max-w-7xl px-6">
    <div
      className="relative"
      style={{
        // band fill with padding only on sides to connect borders
        background: colors.accentLight,
        // dotted separators that extend full width to connect with rails
        borderTop: `1.5px dotted ${colors.accent}`,
        borderBottom: `1.5px dotted ${colors.accent}`,
        // minimal vertical padding to tightly frame buttons (py-4 = 16px + border = ~56px total button height)
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '24px',  // px-6 equivalent to maintain content spacing
        paddingRight: '24px',
        // extend borders to connect with rails
        marginLeft: 'calc(-24px - 6px)',   // 24px container padding + 6px inner-rail offset
        marginRight: 'calc(-24px - 6px)',
        // keep it subtle
        boxShadow: 'inset 0 0 0 0 rgba(0,0,0,0)'
      }}
    >
      <div className="relative z-10 flex justify-center">
        {children}
      </div>
    </div>
  </div>
);

const TabbedWorkflowSection = () => {
  const [activeTab, setActiveTab] = useState('signature');
  const [selectedPreSignatureDemo, setSelectedPreSignatureDemo] = useState('assembly');
  const [selectedSignatureDemo, setSelectedSignatureDemo] = useState('complete-experience');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const tabs = ['pre-signature', 'signature'];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Reset demo selections when switching tabs
    if (tabId === 'pre-signature') {
      setSelectedPreSignatureDemo('assembly');
    } else if (tabId === 'signature') {
      setSelectedSignatureDemo('complete-experience');
    }
  };

  const handlePreSignatureDemoChange = (demoType) => {
    setSelectedPreSignatureDemo(demoType);
  };

  const handleSignatureDemoChange = (demoType) => {
    setSelectedSignatureDemo(demoType);
  };


  const renderDemo = () => {
    switch (activeTab) {
      case 'pre-signature':
        return <PreSignatureDemo selectedFeature={selectedPreSignatureDemo} onFeatureChange={handlePreSignatureDemoChange} />;
      case 'signature':
        return <SignatureDemo selectedFeature={selectedSignatureDemo} onFeatureChange={handleSignatureDemoChange} />;
      default:
        return <SignatureDemo selectedFeature={selectedSignatureDemo} onFeatureChange={handleSignatureDemoChange} />;
    }
  };

  return (
    <motion.section
      ref={ref}
      className="py-20 relative overflow-hidden"
      style={{
        backgroundColor: colors.white,
        fontFamily: fontStyles.fontFamily,
        background: 'linear-gradient(to bottom, white, #f8faff)'
      }}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      id="workflow-section"
    >
      <GridScaffold
  containerMax="max-w-7xl"
  containerPx={24}
  railColor="#1e40af"
  railOpacity={0.25}
  railWidth={1.25}
  railStyle="solid"
  railDouble
  railDoubleGap={17}
  showSecondaryGrid={false}
  gridGap={120}
  gridColor="#1e40af"
  gridOpacity={0.08}
  showRows={false}
  rowStep={120}
  rowColor="#1e40af"
  rowOpacity={0.06}
  showDotOverlay={false}
  showHatchGutters={true}
  hatchAngleDeg={135}
  hatchGap={32}
  hatchOpacity={0.11}
  hideOnMobile={false}
/>


      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-8"
          variants={itemVariants}
        >
          <motion.div 
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6" 
            style={{
              backgroundColor: colors.accentLight, 
              color: colors.accent,
              ...fontStyles.medium
            }}
            whileHover={{ 
              scale: 1.02,
              backgroundColor: `rgba(30, 64, 175, 0.15)` 
            }}
          >
            Contract Generation & E-Signature
          </motion.div>
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-6" 
            style={{
              ...fontStyles.heading,
              letterSpacing: '-0.04em',
              lineHeight: '1.1',
              color: colors.gray900
            }}
            variants={itemVariants}
          >
            Contract generation and signatures.<br />
            <span style={{ color: colors.accent }}>Built for small businesses and freelancers.</span>
          </motion.h2>
          <motion.p 
            className="text-xl max-w-3xl mx-auto" 
            style={{
              color: colors.gray700,
              ...fontStyles.body,
              lineHeight: '1.5'
            }}
            variants={itemVariants}
          >
            From instant contract generation to lightning-fast signatures, SignPact provides everything 
            small businesses and freelancers need to manage contracts professionally.
          </motion.p>
        </motion.div>

        {/* Tab Navigation inside the band */}
        <motion.div className="mb-12" variants={itemVariants}>
          <BandedTabs>
            <div className="inline-flex gap-3 relative">
              {tabs.map((tabId) => {
                const isActive = activeTab === tabId;
                const content = tabContent[tabId];
                return (
                  <motion.button
                    key={tabId}
                    onClick={() => handleTabChange(tabId)}
                    className="relative px-8 py-4 font-medium flex items-center justify-center transition-all duration-300 ease-out overflow-hidden group"
                    style={{
                      borderRadius: '20px',
                      background: isActive 
                        ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                      color: isActive ? colors.white : colors.gray700,
                      boxShadow: isActive 
                        ? '0 10px 30px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                        : '0 8px 25px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                      ...fontStyles.medium,
                      letterSpacing: '-0.01em',
                      fontSize: '16px',
                      textShadow: isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                      border: isActive 
                        ? `1px solid rgba(255, 255, 255, 0.1)`
                        : '1px solid rgba(30, 64, 175, 0.15)',
                      backdropFilter: isActive ? 'none' : 'blur(12px)'
                    }}
                    whileHover={{ 
                      scale: 1.03,
                      y: -2
                    }}
                    whileTap={{ scale: 0.98 }}
                    onMouseEnter={(e) => {
                      if (isActive) {
                        e.target.style.boxShadow = '0 15px 40px rgba(30, 64, 175, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                        e.target.style.filter = 'brightness(1.05)';
                      } else {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)';
                        e.target.style.borderColor = 'rgba(30, 64, 175, 0.25)';
                        e.target.style.color = colors.accent;
                        e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                        e.target.style.filter = 'brightness(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) {
                        e.target.style.boxShadow = '0 10px 30px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                        e.target.style.filter = 'brightness(1)';
                      } else {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
                        e.target.style.borderColor = 'rgba(30, 64, 175, 0.15)';
                        e.target.style.color = colors.gray700;
                        e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                        e.target.style.filter = 'brightness(1)';
                      }
                    }}
                  >
                    {/* Shimmer effect for active button */}
                    {isActive && (
                      <div 
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                          background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                          borderRadius: '20px'
                        }}
                      />
                    )}
                    
                    {/* Subtle gradient overlay for inactive buttons */}
                    {!isActive && (
                      <div 
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                        style={{
                          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, transparent 50%)',
                          borderRadius: '20px'
                        }}
                      />
                    )}
                    
                    <span className="relative z-10">{content.title}</span>
                  </motion.button>
                );
              })}
            </div>
          </BandedTabs>
        </motion.div>

        {/* Content Area - Dynamic Layout Based on Demo Type */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`${
              (activeTab === 'signature' && selectedSignatureDemo === 'complete-experience') || activeTab === 'pre-signature'
                ? 'w-full' // Full width for primary signature demo and pre-signature
                : 'grid lg:grid-cols-5 gap-8 lg:gap-12 items-start' // Standard grid for others
            } relative z-10`}
            style={{
              padding: '0 24px', // 24px spacing system
              maxWidth: '1280px', // 7xl equivalent
              margin: '0 auto'
            }}
          >
            {/* Full-Width Demo Layout for Primary Signature Demo ONLY */}
            {activeTab === 'signature' && selectedSignatureDemo === 'complete-experience' ? (
              <div className="w-full">
                {/* Header Section */}
                <div className="mb-12">
                  <div className="max-w-4xl">
                    <h3 
                      className="text-3xl font-bold mb-4 leading-tight"
                      style={{ color: colors.gray900, ...fontStyles.heading }}
                    >
                      {tabContent[activeTab].subtitle}
                    </h3>
                    <p 
                      className="text-xl leading-relaxed"
                      style={{ color: colors.gray600, ...fontStyles.body }}
                    >
                      {tabContent[activeTab].description}
                    </p>
                  </div>
                </div>

                {/* Full-Width Demo */}
                <DemoContainer title="Complete Signing Experience">
                  {renderDemo()}
                </DemoContainer>

                {/* Feature Cards Below Demo */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {tabContent[activeTab].features.map((feature, index) => (
                    <FeatureCard
                      key={feature.title}
                      icon={feature.icon}
                      title={feature.title}
                      description={feature.description}
                      delay={index * 0.1}
                      demoType={feature.demoType}
                      selectedDemo={selectedSignatureDemo}
                      onSelectDemo={handleSignatureDemoChange}
                      isActive={selectedSignatureDemo === feature.demoType}
                    />
                  ))}
                </div>

                {/* CTA Button */}
                {tabContent[activeTab].ctaButton && (
                  <motion.div 
                    className="mt-8 pt-6 border-t"
                    style={{ borderColor: colors.border }}
                    variants={itemVariants}
                  >
                    <div className="text-center">
                      <motion.a
                        href={tabContent[activeTab].ctaButton.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-8 py-4 rounded-2xl text-white font-semibold text-lg shadow-lg transition-all duration-300"
                        style={{
                          background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                          boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)',
                          ...fontStyles.heading
                        }}
                        whileHover={{ 
                          scale: 1.05,
                          boxShadow: '0 15px 40px rgba(30, 64, 175, 0.4)',
                          y: -2
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Zap size={20} className="mr-2" />
                        {tabContent[activeTab].ctaButton.text}
                        <ArrowRight size={20} className="ml-2" />
                      </motion.a>
                    </div>
                  </motion.div>
                )}
              </div>
            ) : activeTab === 'pre-signature' ? (
              /* Pre-Signature: Full-Width Demo */
              <div className="w-full">
                {/* Header Section */}
                <div className="mb-12 text-center">
                  <h3 
                    className="text-3xl font-bold mb-4 leading-tight"
                    style={{ color: colors.gray900, ...fontStyles.heading }}
                  >
                    {tabContent[activeTab].subtitle}
                  </h3>
                  <p 
                    className="text-xl leading-relaxed max-w-3xl mx-auto"
                    style={{ color: colors.gray600, ...fontStyles.body }}
                  >
                    {tabContent[activeTab].description}
                  </p>
                </div>
                
                {/* Full-Width Demo Container */}
                <DemoContainer title="AI Contract Generation">
                  {renderDemo()}
                </DemoContainer>
              </div>
            ) : (
              <>
                {/* Standard Two-Column Layout for Other Demos */}
                {/* Left Column - Feature Cards */}
                <div className="lg:col-span-2 order-2 lg:order-1">
                  <div className="mb-8">
                    <h3 
                      className="text-3xl font-bold mb-4 leading-tight"
                      style={{ color: colors.gray900, ...fontStyles.heading }}
                    >
                      {tabContent[activeTab].subtitle}
                    </h3>
                    <p 
                      className="text-xl leading-relaxed"
                      style={{ color: colors.gray600, ...fontStyles.body }}
                    >
                      {tabContent[activeTab].description}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-6" style={{ gap: '24px' }}>
                    {tabContent[activeTab].features.map((feature, index) => (
                      <FeatureCard
                        key={feature.title}
                        icon={feature.icon}
                        title={feature.title}
                        description={feature.description}
                        delay={index * 0.1}
                        demoType={feature.demoType}
                        selectedDemo={
                          activeTab === 'pre-signature' ? selectedPreSignatureDemo :
                          activeTab === 'signature' ? selectedSignatureDemo :
                          null
                        }
                        onSelectDemo={
                          activeTab === 'pre-signature' ? handlePreSignatureDemoChange :
                          activeTab === 'signature' ? handleSignatureDemoChange : null
                        }
                        isActive={
                          (activeTab === 'pre-signature' && selectedPreSignatureDemo === feature.demoType) ||
                          (activeTab === 'signature' && selectedSignatureDemo === feature.demoType)
                        }
                      />
                    ))}
                  </div>
                  
                  {/* CTA Button for Signature Tab */}
                  {activeTab === 'signature' && tabContent[activeTab].ctaButton && (
                    <motion.div 
                      className="mt-8 pt-6 border-t"
                      style={{ borderColor: colors.border }}
                      variants={itemVariants}
                    >
                      <div className="text-center">
                        <motion.a
                          href={tabContent[activeTab].ctaButton.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-8 py-4 rounded-2xl text-white font-semibold text-lg shadow-lg transition-all duration-300"
                          style={{
                            background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                            boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)',
                            ...fontStyles.heading
                          }}
                          whileHover={{ 
                            scale: 1.05,
                            boxShadow: '0 15px 40px rgba(30, 64, 175, 0.4)',
                            y: -2
                          }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Zap size={20} className="mr-2" />
                          {tabContent[activeTab].ctaButton.text}
                          <ArrowRight size={20} className="ml-2" />
                        </motion.a>
                      </div>
                    </motion.div>
                  )}
                </div>
                
                {/* Right Column - Interactive Demo */}
                <div className="lg:col-span-3 order-1 lg:order-2">
                  <DemoContainer title={tabContent[activeTab].subtitle}>
                    {renderDemo()}
                  </DemoContainer>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.section>
  );
};

export default TabbedWorkflowSection; 